import type { Identifier } from "ra-core";

export {
  BROADCAST_FOOTER,
  INTERESTED_BUTTON,
  NAME_TOKEN,
  OPT_OUT_BUTTON,
  bodyProblems,
  broadcastText,
} from "../../../../../supabase/functions/_shared/broadcastTemplate.ts";

export const BROADCASTS_PATH = "/whatsapp/difusiones";

/** Meta's price of a marketing message to Spain, roughly (VAT excluded). */
export const COST_PER_MESSAGE_EUR = 0.07;
/** Days a person waits between two broadcasts (same as the database). */
export const GAP_DAYS = 3;

export type BroadcastStatus =
  | "draft"
  | "submitted"
  | "approved"
  | "rejected"
  | "sending"
  | "sent"
  | "cancelled";

export type BroadcastSegment = "leads" | "clients";

export interface BroadcastFilters {
  stages?: string[];
  temperatures?: string[];
  zones?: string[];
  quiet_days?: number | null;
}

/** A row of gula_broadcasts_summary. */
export interface Broadcast {
  id: Identifier;
  title: string;
  status: BroadcastStatus;
  segment: BroadcastSegment;
  filters: BroadcastFilters;
  material_id?: Identifier | null;
  body: string;
  template_name?: string | null;
  template_status?: string | null;
  template_reason?: string | null;
  scheduled_at?: string | null;
  created_at: string;
  started_at?: string | null;
  finished_at?: string | null;
  material_title?: string | null;
  material_kind?: "image" | "video" | "document" | null;
  material_url?: string | null;
  total?: number;
  sent?: number;
  failed?: number;
  delivered?: number;
  read?: number;
  replied?: number;
}

export const STATUS_LABEL: Record<
  BroadcastStatus,
  { label: string; className: string }
> = {
  draft: { label: "Borrador", className: "bg-muted text-muted-foreground" },
  submitted: {
    label: "Meta lo está revisando",
    className: "bg-amber-100 text-amber-900",
  },
  approved: {
    label: "Aprobada · programada",
    className: "bg-lime-200 text-neutral-900",
  },
  rejected: {
    label: "Meta la ha rechazado",
    className: "bg-red-100 text-red-800",
  },
  sending: { label: "Enviando", className: "bg-sky-100 text-sky-900" },
  sent: { label: "Enviada", className: "bg-emerald-100 text-emerald-900" },
  cancelled: {
    label: "Cancelada",
    className: "bg-muted text-muted-foreground",
  },
};

/** Stages a broadcast to leads can target (everything but "Compra"). */
export const LEAD_STAGE_OPTIONS = [
  { value: "new", label: "Nuevo" },
  { value: "no_answer", label: "No contesta" },
  { value: "first_contact", label: "1er contacto" },
  { value: "second_contact", label: "2º contacto" },
  { value: "recontact", label: "Recontactar" },
  { value: "sure_purchase", label: "Compra segura" },
  { value: "no_purchase", label: "No compra" },
  { value: "reactivation", label: "Reactivar" },
];

export const TEMPERATURE_OPTIONS = [
  { value: "hot", label: "Caliente" },
  { value: "warm", label: "Templado" },
  { value: "cold", label: "Frío" },
  { value: "none", label: "Sin temperatura" },
];

export const DEFAULT_BODY =
  "Hola {nombre}, soy de Gula de Agua 💧\n\nTe dejamos esto para que veas cómo es tener agua de ósmosis en casa, sin garrafas.\n\nSi te interesa, pulsa «Me interesa» y te llamamos cuando mejor te venga.";

/** Editable draft (what the form holds). */
export interface BroadcastDraft {
  id?: Identifier;
  title: string;
  segment: BroadcastSegment;
  filters: BroadcastFilters;
  material_id: Identifier | null;
  body: string;
  scheduled_at: string; // datetime-local value (Madrid)
}

/** Tomorrow at 11:00 local time, as a datetime-local value. */
export const defaultSchedule = (now = new Date()): string => {
  const date = new Date(now);
  date.setDate(date.getDate() + 1);
  date.setHours(11, 0, 0, 0);
  return toLocalInput(date.toISOString());
};

/** ISO -> "YYYY-MM-DDTHH:mm" in local time (for datetime-local inputs). */
export const toLocalInput = (iso: string): string => {
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const newDraft = (): BroadcastDraft => ({
  title: "",
  segment: "leads",
  filters: { stages: [], temperatures: [], zones: [], quiet_days: null },
  material_id: null,
  body: DEFAULT_BODY,
  scheduled_at: defaultSchedule(),
});

export const draftOf = (broadcast: Broadcast): BroadcastDraft => ({
  id: broadcast.id,
  title: broadcast.title,
  segment: broadcast.segment,
  filters: {
    stages: broadcast.filters?.stages ?? [],
    temperatures: broadcast.filters?.temperatures ?? [],
    zones: broadcast.filters?.zones ?? [],
    quiet_days: broadcast.filters?.quiet_days ?? null,
  },
  material_id: broadcast.material_id ?? null,
  body: broadcast.body,
  scheduled_at: broadcast.scheduled_at
    ? toLocalInput(broadcast.scheduled_at)
    : defaultSchedule(),
});

/** Filters as stored: empty lists and empty days left out. */
export const cleanFilters = (filters: BroadcastFilters): BroadcastFilters => ({
  ...(filters.stages?.length ? { stages: filters.stages } : {}),
  ...(filters.temperatures?.length
    ? { temperatures: filters.temperatures }
    : {}),
  ...(filters.zones?.length ? { zones: filters.zones } : {}),
  ...(filters.quiet_days ? { quiet_days: filters.quiet_days } : {}),
});

export const estimatedCost = (people: number) =>
  (people * COST_PER_MESSAGE_EUR).toLocaleString("es-ES", {
    style: "currency",
    currency: "EUR",
  });

/** Problems that stop a draft from going to Meta, besides the text. */
export const draftProblems = (draft: BroadcastDraft, now = new Date()) => {
  const problems: string[] = [];
  if (!draft.title.trim()) problems.push("Ponle un nombre a la difusión");
  if (!draft.scheduled_at) problems.push("Elige el día y la hora");
  else if (new Date(draft.scheduled_at).getTime() < now.getTime() - 60_000)
    problems.push("La fecha ya ha pasado");
  return problems;
};

export type BroadcastGroup = { key: string; title: string; items: Broadcast[] };

const dayTitle = (iso: string) =>
  new Date(iso).toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

/**
 * Calendar view: upcoming broadcasts by day (soonest first), then drafts and
 * rejected ones, then the ones already sent or cancelled (latest first).
 */
export const groupBroadcasts = (broadcasts: Broadcast[]): BroadcastGroup[] => {
  const upcoming = broadcasts
    .filter((b) => ["submitted", "approved", "sending"].includes(b.status))
    .sort((a, b) => (a.scheduled_at ?? "").localeCompare(b.scheduled_at ?? ""));
  const byDay = new Map<string, Broadcast[]>();
  for (const broadcast of upcoming) {
    const key = (broadcast.scheduled_at ?? "").slice(0, 10) || "sin fecha";
    byDay.set(key, [...(byDay.get(key) ?? []), broadcast]);
  }
  const groups: BroadcastGroup[] = [...byDay.entries()].map(([key, items]) => ({
    key,
    title: items[0].scheduled_at
      ? dayTitle(items[0].scheduled_at)
      : "Sin fecha",
    items,
  }));
  const drafts = broadcasts.filter((b) =>
    ["draft", "rejected"].includes(b.status),
  );
  if (drafts.length)
    groups.push({ key: "drafts", title: "Borradores", items: drafts });
  const done = broadcasts
    .filter((b) => ["sent", "cancelled"].includes(b.status))
    .sort((a, b) =>
      (b.scheduled_at ?? b.created_at).localeCompare(
        a.scheduled_at ?? a.created_at,
      ),
    );
  if (done.length) groups.push({ key: "done", title: "Enviadas", items: done });
  return groups;
};
